import { Printer, WifiOff } from 'lucide-react'
import { formatDateTime, formatMoney, formatNumber } from '@najah/core'
import { Alert, Button, Dialog } from '@najah/ui'

export interface ReceiptData {
  reference: string
  occurredAt: string
  items: { nameSnapshot: string; quantity: number; unitPrice: number; lineTotal: number; unitSnapshot: string }[]
  subtotal: number
  discountTotal: number
  taxTotal: number
  total: number
  paid: number
  change: number
  cashierName: string
  storeName: string
  queued: boolean
}

/**
 * إيصال للطباعة. يستعمل طباعة المتصفّح بعرض ٨٠مم —
 * الطباعة الحرارية المباشرة (ESC/POS) تحتاج معرفة طراز الطابعة.
 */
export function ReceiptDialog({ open, onClose, receipt }: { open: boolean; onClose: () => void; receipt: ReceiptData | null }) {
  if (!receipt) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="تمّ البيع"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>إغلاق</Button>
          <Button onClick={() => window.print()} startIcon={<Printer className="size-4" />}>طباعة</Button>
        </>
      }
    >
      <div className="space-y-4">
        {receipt.queued && (
          <Alert tone="warning" title="محفوظة محلياً">
            <span className="inline-flex items-center gap-1.5">
              <WifiOff className="size-3.5" />
              لا يوجد اتصال — الفاتورة مسجّلة على الجهاز وستُرسل تلقائياً عند عودة الشبكة.
            </span>
          </Alert>
        )}

        {/* منطقة الطباعة: تُعزل عن بقية الصفحة في وضع الطباعة */}
        <div id="najah-receipt" className="rounded-lg border border-border bg-white p-4 font-mono text-[11px] text-black">
          <div className="text-center">
            <p className="text-sm font-bold">{receipt.storeName}</p>
            <p className="mt-0.5 text-[10px]">مكتبة وقرطاسية</p>
          </div>

          <div className="my-2 border-t border-dashed border-black/30" />

          <div className="flex justify-between text-[10px]">
            <span dir="ltr">{receipt.reference}</span>
            <span>{formatDateTime(receipt.occurredAt)}</span>
          </div>
          <p className="text-[10px]">الكاشير: {receipt.cashierName}</p>

          <div className="my-2 border-t border-dashed border-black/30" />

          <table className="w-full">
            <tbody>
              {receipt.items.map((item, i) => (
                <tr key={i}>
                  <td className="py-0.5 align-top">
                    {item.nameSnapshot}
                    <br />
                    <span className="text-[9px] opacity-70" dir="ltr">
                      {formatNumber(item.quantity)} × {formatMoney(item.unitPrice, { withSymbol: false })}
                    </span>
                  </td>
                  <td className="py-0.5 text-end align-top whitespace-nowrap" dir="ltr">
                    {formatMoney(item.lineTotal, { withSymbol: false })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="my-2 border-t border-dashed border-black/30" />

          <div className="space-y-0.5">
            <div className="flex justify-between">
              <span>المجموع</span>
              <span dir="ltr">{formatMoney(receipt.subtotal, { withSymbol: false })}</span>
            </div>
            {receipt.discountTotal > 0 && (
              <div className="flex justify-between">
                <span>الخصم</span>
                <span dir="ltr">−{formatMoney(receipt.discountTotal, { withSymbol: false })}</span>
              </div>
            )}
            {receipt.taxTotal > 0 && (
              <div className="flex justify-between">
                <span>الضريبة</span>
                <span dir="ltr">{formatMoney(receipt.taxTotal, { withSymbol: false })}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-black/30 pt-1 text-sm font-bold">
              <span>الإجمالي</span>
              <span dir="ltr">{formatMoney(receipt.total)}</span>
            </div>
            <div className="flex justify-between">
              <span>المدفوع</span>
              <span dir="ltr">{formatMoney(receipt.paid, { withSymbol: false })}</span>
            </div>
            {receipt.change > 0 && (
              <div className="flex justify-between">
                <span>الباقي</span>
                <span dir="ltr">{formatMoney(receipt.change, { withSymbol: false })}</span>
              </div>
            )}
          </div>

          <div className="my-2 border-t border-dashed border-black/30" />
          <p className="text-center text-[10px]">شكراً لتعاملكم معنا</p>
        </div>
      </div>
    </Dialog>
  )
}
